/**
 * useBlocks Hook
 *
 * Custom hook for managing blocks data with pagination and search.
 */

import * as React from "react";
import { blockService, type Block } from "@/services/blockService";
import { toast } from "sonner";

export interface BlocksState {
  data: Block[];
  nextCursor: string | null;
  previousCursor: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
}

export function useBlocks() {
  const [state, setState] = React.useState<BlocksState>({
    data: [],
    nextCursor: null,
    previousCursor: null,
    hasNext: false,
    hasPrevious: false,
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch blocks function with pagination
  const fetchBlocks = React.useCallback(async (options?: {
    cursor?: string;
    direction?: "next" | "previous";
    search?: string;
    sortBy?: "callsign" | "nama" | "province" | "blockedAt" | "_id";
    sortOrder?: "asc" | "desc";
  }) => {
    setLoading(true);
    setError(null);

    try {
      const result = await blockService.fetchBlocksWithPagination(options || {});
      const blocks = result.data || result.blocks || [];
      setState({
        data: blocks,
        nextCursor: result.nextCursor || null,
        previousCursor: result.previousCursor || null,
        hasNext: result.hasNext || false,
        hasPrevious: result.hasPrevious || false,
      });
    } catch (err) {
      console.error("Error fetching blocks:", err);
      const errorMessage = err instanceof Error ? err.message : "Gagal memuat data block";
      setError(errorMessage);
      setState({
        data: [],
        nextCursor: null,
        previousCursor: null,
        hasNext: false,
        hasPrevious: false,
      });
      toast.error("Gagal memuat data block", {
        description: err instanceof Error ? err.message : "Terjadi kesalahan saat memuat data",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch blocks on mount
  React.useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  return {
    blocks: state.data,
    ...state,
    loading,
    error,
    fetchBlocks,
  };
}

